import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { householdAPI } from '../services/api';
import './Onboarding.css';

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pot creation state
  const [pots, setPots] = useState([
    { name: '', currency: 'EUR', icon: '🫙' }
  ]);

  // Household state
  const [householdChoice, setHouseholdChoice] = useState(null); // 'create', 'join', 'skip'
  const [householdName, setHouseholdName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [householdSuccess, setHouseholdSuccess] = useState(null);

  const currencies = ['EUR', 'GBP', 'USD', 'CHF', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];
  const potIcons = ['🫙', '💰', '🏦', '💵', '💳', '🐷', '💎', '🎯', '🏠', '✈️', '🎓', '🚗'];

  // --- STEP 1: Pots ---
  const addPot = () => {
    setPots([...pots, { name: '', currency: 'EUR', icon: '🫙' }]);
  };

  const removePot = (index) => {
    if (pots.length > 1) {
      setPots(pots.filter((_, i) => i !== index));
    }
  };

  const updatePot = (index, field, value) => {
    const updated = [...pots];
    updated[index][field] = value;
    setPots(updated);
  };

  const handleCreatePots = async () => {
    // Validate at least one pot has a name
    const validPots = pots.filter(p => p.name.trim());
    if (validPots.length === 0) {
      setError('Please add at least one pot with a name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      for (let i = 0; i < validPots.length; i++) {
        const pot = validPots[i];
        await api.post('/wallets', {
          name: pot.name,
          currency: pot.currency,
          icon: pot.icon,
          is_default: i === 0 // First pot is default
        });
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create pots');
    } finally {
      setLoading(false);
    }
  };

  // --- STEP 2: Household ---
  const handleCreateHousehold = async () => {
    if (!householdName.trim()) {
      setError('Please enter a household name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await householdAPI.create(householdName);
      setHouseholdSuccess({
        type: 'created',
        name: response.data.name,
        inviteCode: response.data.invite_code
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create household');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinHousehold = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await householdAPI.join(inviteCode);
      setHouseholdSuccess({
        type: 'joined',
        status: response.data.status,
        message: response.data.message
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to join household');
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = () => {
    // Force full page reload so WalletContext loads the new pots
    window.location.href = '/analytics';
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {/* Progress indicator */}
        <div className="onboarding-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Create Pots</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Household</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* STEP 1: Create Pots */}
        {step === 1 && (
          <div className="onboarding-step">
            <h2>🫙 Create Your Pots</h2>
            <p className="step-description">
              Pots help you organize your money. Create at least one to get started.
            </p>

            <div className="pots-list">
              {pots.map((pot, index) => (
                <div key={index} className="pot-row">
                  <select
                    value={pot.icon}
                    onChange={(e) => updatePot(index, 'icon', e.target.value)}
                    className="pot-icon-select"
                  >
                    {potIcons.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={pot.name}
                    onChange={(e) => updatePot(index, 'name', e.target.value)}
                    placeholder="Pot name (e.g., Main Account)"
                    className="pot-name-input"
                  />
                  <select
                    value={pot.currency}
                    onChange={(e) => updatePot(index, 'currency', e.target.value)}
                    className="pot-currency-select"
                  >
                    {currencies.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {pots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePot(index)}
                      className="remove-pot-btn"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button type="button" onClick={addPot} className="add-pot-btn">
              + Add Another Pot
            </button>

            <div className="onboarding-actions">
              <button
                onClick={handleCreatePots}
                disabled={loading}
                className="btn-primary"
              >
                {loading ? 'Creating...' : 'Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Household */}
        {step === 2 && !householdSuccess && (
          <div className="onboarding-step">
            <h2>🏠 Household Setup</h2>
            <p className="step-description">
              Share expenses with family or roommates? Set up a household to split costs easily.
            </p>

            {!householdChoice && (
              <div className="household-choices">
                <button
                  className="choice-card"
                  onClick={() => setHouseholdChoice('create')}
                >
                  <span className="choice-icon">🏡</span>
                  <span className="choice-title">Create Household</span>
                  <span className="choice-desc">Start a new household and invite others</span>
                </button>
                <button
                  className="choice-card"
                  onClick={() => setHouseholdChoice('join')}
                >
                  <span className="choice-icon">🔗</span>
                  <span className="choice-title">Join Household</span>
                  <span className="choice-desc">I have an invite code</span>
                </button>
                <button
                  className="choice-card skip"
                  onClick={finishOnboarding}
                >
                  <span className="choice-icon">⏭️</span>
                  <span className="choice-title">Skip for Now</span>
                  <span className="choice-desc">I'll set this up later</span>
                </button>
              </div>
            )}

            {householdChoice === 'create' && (
              <div className="household-form">
                <button className="back-btn" onClick={() => setHouseholdChoice(null)}>
                  ← Back
                </button>
                <div className="form-group">
                  <label>Household Name</label>
                  <input
                    type="text"
                    value={householdName}
                    onChange={(e) => setHouseholdName(e.target.value)}
                    placeholder="e.g., Smith Family"
                    autoFocus
                  />
                </div>
                <div className="onboarding-actions">
                  <button
                    onClick={handleCreateHousehold}
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Creating...' : 'Create Household'}
                  </button>
                </div>
              </div>
            )}

            {householdChoice === 'join' && (
              <div className="household-form">
                <button className="back-btn" onClick={() => setHouseholdChoice(null)}>
                  ← Back
                </button>
                <div className="form-group">
                  <label>Invite Code</label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    placeholder="Enter the code from your household"
                    autoFocus
                  />
                </div>
                <div className="onboarding-actions">
                  <button
                    onClick={handleJoinHousehold}
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Joining...' : 'Request to Join'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Household Success */}
        {step === 2 && householdSuccess && (
          <div className="onboarding-step success-step">
            {householdSuccess.type === 'created' && (
              <>
                <div className="success-icon">🎉</div>
                <h2>Household Created!</h2>
                <p>Your household "<strong>{householdSuccess.name}</strong>" is ready.</p>
                <div className="invite-code-box">
                  <span className="invite-label">Share this invite code:</span>
                  <span className="invite-code">{householdSuccess.inviteCode}</span>
                </div>
                <p className="small-text">Others can use this code to join your household.</p>
              </>
            )}

            {householdSuccess.type === 'joined' && (
              <>
                <div className="success-icon">⏳</div>
                <h2>Request Sent!</h2>
                <p>{householdSuccess.message}</p>
                <p className="small-text">You'll be notified once the owner approves your request.</p>
              </>
            )}

            <div className="onboarding-actions">
              <button onClick={finishOnboarding} className="btn-primary">
                Go to Dashboard →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Onboarding;
